<template>
	<v-row class="pb-offset ma-0">
		<v-col class="pa-0">
			<v-card elevation="1" class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-3">Brand</p>
					<v-spacer></v-spacer>
					<v-btn small depressed color="primary" :disabled="loading" @click="openEditor(null)">
						<v-icon small left>mdi-plus</v-icon>Nuovo brand
					</v-btn>
				</v-card-title>
				<v-divider></v-divider>

				<v-container class="pa-6" fluid>
					<div class="caption grey--text mb-4">
						L'identità con cui l'assistente del builder compone le mail: logo, colori, font, contatti, tono e dati legali del footer.
						Una mail fatta con un brand usa solo i dati del brand.
					</div>

					<div v-if="loading" class="text-center py-8">
						<v-progress-circular indeterminate color="primary"></v-progress-circular>
					</div>

					<v-alert v-else-if="!brands.length" type="info" text class="mb-0">
						Nessun brand: le mail usano i dati dell'azienda. Crea un brand se vendi con più marchi,
						o per dare all'assistente logo, colori e tono di voce.
					</v-alert>

					<v-row v-else>
						<v-col v-for="brand in brands" :key="brand.id" cols="12" md="6" lg="4">
							<v-card outlined class="brand-card" @click="openEditor(brand)">
								<div class="brand-card__logos">
									<div class="brand-card__logo brand-card__logo--light">
										<img v-if="brand.logo_light_url" :src="brand.logo_light_url" alt="">
										<span v-else-if="!brand.logo_dark_url" class="brand-card__initial">{{ brand.name.charAt(0) }}</span>
									</div>
									<div v-if="brand.logo_dark_url" class="brand-card__logo brand-card__logo--dark">
										<img :src="brand.logo_dark_url" alt="">
									</div>
								</div>
								<div class="brand-card__body">
									<div class="d-flex align-center">
										<span class="brand-card__name">{{ brand.name }}</span>
										<v-chip v-if="brand.is_default" x-small label class="ml-2">Predefinito</v-chip>
										<v-spacer></v-spacer>
										<v-btn icon small title="Elimina" @click.stop="askDelete(brand)">
											<v-icon small>mdi-delete-outline</v-icon>
										</v-btn>
									</div>
									<div class="brand-card__meta">
										<span v-for="(c, i) in [brand.primary_color, brand.secondary_color].filter(Boolean)" :key="i"
											class="brand-card__swatch" :style="{ background: c }"></span>
										<span>{{ fontName(brand.font) }}</span>
									</div>
								</div>
							</v-card>
						</v-col>
					</v-row>
				</v-container>
			</v-card>
		</v-col>

		<!-- Creazione / modifica -->
		<v-dialog v-model="editor" max-width="780" scrollable persistent>
			<v-card v-if="form" class="brand-editor background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">{{ form.id ? 'Modifica brand' : 'Nuovo brand' }}</p>
					<v-spacer></v-spacer>
					<v-btn icon @click="editor = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-divider></v-divider>

				<v-card-text class="pt-2 pb-4">
					<div class="be-section">Identità</div>
					<v-row dense>
						<v-col cols="12" sm="7">
							<label class="pp-label">Nome *</label>
							<v-text-field v-model="form.name" dense outlined hide-details="auto" :error-messages="fieldErrors.name"></v-text-field>
						</v-col>
						<v-col cols="12" sm="5" class="d-flex align-end">
							<v-switch v-model="form.is_default" inset dense hide-details class="mt-0 mb-2" :disabled="lockedDefault"
								label="Predefinito"></v-switch>
						</v-col>
					</v-row>
					<div class="pp-hint">
						{{ lockedDefault ? 'Per cambiarlo, rendi predefinito un altro brand.' : 'Il predefinito è quello proposto a ogni mail nuova.' }}
					</div>

					<div class="be-section">Logo</div>
					<v-row dense>
						<v-col v-for="slot in logoSlots" :key="slot.key" cols="12" sm="6">
							<label class="pp-label">{{ slot.label }}</label>
							<div class="be-logo" :class="'be-logo--' + slot.tone">
								<img v-if="form[slot.url]" :src="form[slot.url]" alt="">
								<span v-else class="be-logo__empty">Nessun logo</span>
							</div>
							<div class="be-logo__actions">
								<v-btn x-small outlined :loading="uploading === slot.key" @click="pickLogo(slot.key)">
									<v-icon x-small left>mdi-upload</v-icon>{{ form[slot.url] ? 'Sostituisci' : 'Carica' }}
								</v-btn>
								<v-btn v-if="form[slot.url]" x-small text @click="clearLogo(slot)">Rimuovi</v-btn>
							</div>
							<div v-if="fieldErrors[slot.id]" class="error--text caption">{{ fieldErrors[slot.id] }}</div>
						</v-col>
					</v-row>
					<div class="pp-hint">
						PNG con sfondo trasparente. Con un solo logo, mettilo nel riquadro del fondo su cui si legge:
						l'assistente non lo metterà mai sull'altro.
					</div>
					<input ref="logoFile" type="file" accept="image/png,image/jpeg,image/webp,image/gif" class="d-none" @change="onLogoChosen">

					<div class="be-section">Aspetto</div>
					<v-row dense>
						<v-col cols="12" sm="4">
							<color-field :value="form.primary_color" label="Colore primario" clearable
								@input="form.primary_color = hex($event)"></color-field>
						</v-col>
						<v-col cols="12" sm="4">
							<color-field :value="form.secondary_color" label="Colore secondario" clearable
								@input="form.secondary_color = hex($event)"></color-field>
						</v-col>
						<v-col cols="12" sm="4">
							<label class="pp-label be-label--color-row">Font</label>
							<v-select v-model="form.font" :items="fonts" :menu-props="{'offset-y': true}" placeholder="Arial" clearable dense outlined hide-details="auto" :error-messages="fieldErrors.font"></v-select>
						</v-col>
					</v-row>
					<div v-if="fieldErrors.primary_color || fieldErrors.secondary_color" class="error--text caption">
						{{ fieldErrors.primary_color || fieldErrors.secondary_color }}
					</div>

					<div class="be-section">Di cosa si occupa e come parla</div>
					<label class="pp-label">Di cosa si occupa</label>
					<v-textarea v-model="form.description" rows="2" auto-grow dense outlined hide-details="auto" counter="2000"
						placeholder="Es. consulenza informatica, servizi cloud e sicurezza per piccole e medie imprese" :error-messages="fieldErrors.description"></v-textarea>
					<label class="pp-label mt-3">Tono di voce</label>
					<v-textarea v-model="form.tone" rows="2" auto-grow dense outlined hide-details="auto" counter="1000"
						placeholder="Es. allegro e rassicurante, dà del tu, frasi brevi" :error-messages="fieldErrors.tone"></v-textarea>

					<div class="be-section">Contatti</div>
					<v-row dense>
						<v-col cols="12" sm="4">
							<label class="pp-label">Sito</label>
							<v-text-field v-model="form.website" dense outlined hide-details="auto" placeholder="Es. www.nomeazienda.it" :error-messages="fieldErrors.website"></v-text-field>
						</v-col>
						<v-col cols="12" sm="4">
							<label class="pp-label">Email</label>
							<v-text-field v-model="form.email" dense outlined hide-details="auto" placeholder="Es. info@nomeazienda.it" :error-messages="fieldErrors.email"></v-text-field>
						</v-col>
						<v-col cols="12" sm="4">
							<label class="pp-label">Telefono</label>
							<v-text-field v-model="form.telephone" dense outlined hide-details="auto" placeholder="Es. 055 1234567" :error-messages="fieldErrors.telephone"></v-text-field>
						</v-col>
						<v-col v-for="net in socialNetworks" :key="net.key" cols="12" sm="6">
							<label class="pp-label">{{ net.label }}</label>
							<v-text-field v-model="form.socials[net.key]" :prepend-inner-icon="net.icon" dense outlined hide-details="auto"
								placeholder="https://" :error-messages="fieldErrors['socials.' + net.key]"></v-text-field>
						</v-col>
					</v-row>

					<div class="be-section">Dati legali del footer</div>
					<div class="pp-hint mt-0 mb-2">
						Vanno nel footer di ogni mail fatta con questo brand. Ragione sociale, partita IVA e sede legale sono obbligatorie;
						REA, capitale sociale (per S.r.l. e S.p.A.) e informativa privacy completano il footer richiesto dalla legge.
					</div>
					<v-row dense>
						<v-col cols="12" sm="8">
							<label class="pp-label">Ragione sociale *</label>
							<v-text-field v-model="form.legal_name" dense outlined hide-details="auto" placeholder="Es. Rossi Servizi S.r.l." :error-messages="fieldErrors.legal_name"></v-text-field>
						</v-col>
						<v-col cols="12" sm="4">
							<label class="pp-label">Partita IVA *</label>
							<v-text-field v-model="form.vat_number" dense outlined hide-details="auto" placeholder="Es. IT01234567890" :error-messages="fieldErrors.vat_number"></v-text-field>
						</v-col>
						<v-col cols="12">
							<label class="pp-label">Sede legale *</label>
							<v-text-field v-model="form.address" dense outlined hide-details="auto" placeholder="Es. Via Roma 1, 50122 Firenze (FI)" :error-messages="fieldErrors.address"></v-text-field>
						</v-col>
						<v-col cols="12" sm="6">
							<label class="pp-label">Numero REA</label>
							<v-text-field v-model="form.rea" dense outlined hide-details="auto" placeholder="Es. FI-123456" :error-messages="fieldErrors.rea"></v-text-field>
						</v-col>
						<v-col cols="12" sm="6">
							<label class="pp-label">Capitale sociale</label>
							<v-text-field v-model="form.share_capital" dense outlined hide-details="auto" placeholder="Es. € 10.000 i.v. (S.r.l. e S.p.A.)" :error-messages="fieldErrors.share_capital"></v-text-field>
						</v-col>
						<v-col cols="12" sm="6">
							<label class="pp-label">Link all'informativa privacy</label>
							<v-text-field v-model="form.privacy_url" dense outlined hide-details="auto" placeholder="https://" :error-messages="fieldErrors.privacy_url"></v-text-field>
						</v-col>
						<v-col cols="12" sm="6">
							<label class="pp-label">Link a termini e condizioni</label>
							<v-text-field v-model="form.terms_url" dense outlined hide-details="auto" placeholder="https:// (condizioni delle offerte)" :error-messages="fieldErrors.terms_url"></v-text-field>
						</v-col>
					</v-row>
				</v-card-text>

				<v-divider></v-divider>
				<v-card-actions class="pa-3">
					<v-spacer></v-spacer>
					<v-btn text @click="editor = false">Annulla</v-btn>
					<v-btn depressed color="primary" :loading="saving" :disabled="!!uploading" @click="save">Salva</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Conferma eliminazione: dialog dell'app, mai quelli del browser -->
		<v-dialog v-model="deleteDialog" max-width="440">
			<v-card v-if="toDelete" class="background-2 round-card">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3">Eliminare «{{ toDelete.name }}»?</p>
				</v-card-title>
				<v-card-text>
					Le mail che lo usano passeranno al brand predefinito.
					<template v-if="toDelete.is_default && brands.length > 1"> Il predefinito diventerà il brand creato per primo fra quelli rimasti.</template>
				</v-card-text>
				<v-card-actions class="pa-3">
					<v-spacer></v-spacer>
					<v-btn text @click="deleteDialog = false">Annulla</v-btn>
					<v-btn depressed color="error" :loading="deleting" @click="remove">Elimina</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-row>
</template>

<script>
import ColorField from '@/components/modules/mailmarketing/builder/ColorField'
import { emailFonts } from '@/components/modules/mailmarketing/builder/emailBlocks'

// Stesse reti del footer del builder: il backend scarta le altre
var SOCIAL_NETWORKS = [
	{ key: 'facebook', label: 'Facebook', icon: 'mdi-facebook' },
	{ key: 'instagram', label: 'Instagram', icon: 'mdi-instagram' },
	{ key: 'linkedin', label: 'LinkedIn', icon: 'mdi-linkedin' },
	{ key: 'twitter', label: 'Twitter / X', icon: 'mdi-twitter' },
	{ key: 'youtube', label: 'YouTube', icon: 'mdi-youtube' },
	{ key: 'pinterest', label: 'Pinterest', icon: 'mdi-pinterest' }
]

var FIELDS = ['name', 'is_default', 'primary_color', 'secondary_color', 'font', 'website', 'email', 'telephone',
	'description', 'tone', 'logo_light_id', 'logo_dark_id', 'legal_name', 'vat_number', 'rea', 'share_capital', 'address',
	'privacy_url', 'terms_url']

/**
 * Impostazioni > Altro > Brand: elenco dei brand del tenant e loro modifica.
 * I loghi passano dagli allegati pubblici (come le immagini del builder): caricati
 * prima di salvare un brand nuovo restano appesi a 0 e il backend li aggancia al salvataggio.
 */
export default {
	name: 'brands-settings',

	components: { ColorField },

	data() {
		return {
			brands: [],
			loading: true,
			editor: false,
			form: null,
			fieldErrors: {},
			saving: false,
			uploading: null,
			uploadSlot: null,
			deleteDialog: false,
			toDelete: null,
			deleting: false,
			fonts: emailFonts,
			socialNetworks: SOCIAL_NETWORKS,
			logoSlots: [
				{ key: 'light', label: 'Logo su fondo chiaro', tone: 'light', id: 'logo_light_id', url: 'logo_light_url' },
				{ key: 'dark', label: 'Logo su fondo scuro', tone: 'dark', id: 'logo_dark_id', url: 'logo_dark_url' }
			]
		}
	},

	computed: {
		// Il predefinito si cambia scegliendone un altro: spegnerlo lascerebbe il tenant senza
		lockedDefault() {
			var original = this.form && this.form.id && this.brands.find(b => b.id === this.form.id)
			return !!(original && original.is_default)
		}
	},

	mounted() {
		this.load()
	},

	methods: {
		load() {
			this.loading = true
			this.axios.get('brands').then(response => {
				this.brands = response.data.data || []
			}).catch(() => {
				this.$store.commit('setSnackbarError')
			}).finally(() => {
				this.loading = false
				this.$store.commit('endProgress')
			})
		},

		fontName(font) {
			var match = font && this.fonts.find(f => f.value === font)
			return match ? match.text.replace(/ \(.*\)$/, '') : ''
		},

		// Il selettore emette #RRGGBBAA: un brand ha colori pieni, l'alfa si scarta
		hex(color) {
			return color ? color.slice(0, 7).toUpperCase() : ''
		},

		openEditor(brand) {
			var form = { id: brand ? brand.id : null, logo_light_url: null, logo_dark_url: null, socials: {} }
			FIELDS.forEach(key => {
				form[key] = brand && brand[key] !== undefined && brand[key] !== null ? brand[key] : ''
			})
			form.is_default = !!(brand ? brand.is_default : !this.brands.length)
			if (brand) {
				form.logo_light_url = brand.logo_light_url
				form.logo_dark_url = brand.logo_dark_url
			}
			SOCIAL_NETWORKS.forEach(net => {
				form.socials[net.key] = (brand && brand.socials && brand.socials[net.key]) || ''
			})
			this.form = form
			this.fieldErrors = {}
			this.editor = true
		},

		pickLogo(slot) {
			this.uploadSlot = slot
			this.$refs.logoFile.click()
		},

		onLogoChosen(e) {
			var file = e.target.files && e.target.files[0]
			e.target.value = ''
			var slot = this.logoSlots.find(s => s.key === this.uploadSlot)
			if (!file || !slot) return

			var data = new FormData()
			data.append('file', file, file.name)
			data.append('attachable_type', 'brand')
			data.append('attachable_id', this.form.id || 0)
			data.append('public', 1)
			data.append('usage', 'email_image')

			// Il logo va sul form da cui e' partito: se nel frattempo se ne e' aperto un altro, non e' suo
			var form = this.form
			this.uploading = slot.key
			this.axios.post('attachments/', data).then(response => {
				form[slot.id] = response.data.data.id
				form[slot.url] = this.$store.getters.baseUrl + '/api/publicfiles/' + response.data.data.path
				if (this.form === form) this.$delete(this.fieldErrors, slot.id)
			}).catch(error => {
				this.snackbarError(error, 'Logo non caricato: controlla formato e dimensione dell\'immagine')
			}).finally(() => {
				this.uploading = null
			})
		},

		clearLogo(slot) {
			this.form[slot.id] = ''
			this.form[slot.url] = null
		},

		save() {
			var payload = { socials: this.form.socials }
			FIELDS.forEach(key => {
				var value = this.form[key]
				payload[key] = typeof value === 'string' ? (value.trim() || null) : value
			})

			this.saving = true
			var request = this.form.id
				? this.axios.put('brands/' + this.form.id, payload)
				: this.axios.post('brands', payload)

			request.then(() => {
				this.editor = false
				this.$store.commit('setSnackbar', { timeout: 3000, text: 'Brand salvato', value: true, color: 'success' })
				this.load()
			}).catch(error => {
				var response = error.response
				if (response && response.status === 422 && response.data.errors) {
					var errors = {}
					Object.keys(response.data.errors).forEach(key => {
						errors[key] = response.data.errors[key][0]
					})
					this.fieldErrors = errors
				}
				// Anche col 422: il campo in errore puo' essere fuori vista nel dialog
				this.snackbarError(error, 'Alcuni campi vanno corretti: sono segnati in rosso')
			}).finally(() => {
				this.saving = false
			})
		},

		// Dati rifiutati (422): un testo che dice cosa fare, altrimenti l'errore generico
		snackbarError(error, text) {
			if (error.response && error.response.status === 422) {
				this.$store.commit('setSnackbar', { timeout: 6000, text: text, value: true, color: 'error' })
			} else {
				this.$store.commit('setSnackbarError')
			}
		},

		askDelete(brand) {
			this.toDelete = brand
			this.deleteDialog = true
		},

		remove() {
			this.deleting = true
			this.axios.post('brands/delete', { ids: [this.toDelete.id] }).then(() => {
				this.deleteDialog = false
				this.load()
			}).catch(() => {
				this.$store.commit('setSnackbarError')
			}).finally(() => {
				this.deleting = false
			})
		}
	}
}
</script>

<style scoped>
.brand-card {
	overflow: hidden;
	cursor: pointer;
}

.brand-card__logos {
	display: flex;
	height: 96px;
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.brand-card__logo {
	flex: 1 1 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 16px;
	min-width: 0;
}

.brand-card__logo img {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
}

.brand-card__logo--light {
	background: #fff;
}

.brand-card__logo--dark {
	background: #1f1f1f;
}

.brand-card__initial {
	font-size: 32px;
	font-weight: 700;
	color: rgba(0, 0, 0, 0.26);
	text-transform: uppercase;
}

.brand-card__body {
	padding: 10px 8px 12px 16px;
}

.brand-card__name {
	font-size: 15px;
	font-weight: 500;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.brand-card__meta {
	display: flex;
	align-items: center;
	gap: 6px;
	min-height: 18px;
	font-size: 12px;
	color: rgba(0, 0, 0, 0.6);
}

.brand-card__swatch {
	width: 16px;
	height: 16px;
	border-radius: 50%;
	box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.2);
}

.be-section {
	margin: 22px 0 8px;
	font-size: 13px;
	font-weight: 600;
	letter-spacing: 0.4px;
	text-transform: uppercase;
	color: rgba(0, 0, 0, 0.6);
}

.be-logo {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 110px;
	padding: 16px;
	border: 1px solid rgba(0, 0, 0, 0.2);
	border-radius: 4px;
}

.be-logo img {
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
}

.be-logo--light {
	background: #fff;
}

.be-logo--dark {
	background: #1f1f1f;
}

.be-logo__empty {
	font-size: 12px;
	font-style: italic;
	color: rgba(0, 0, 0, 0.45);
}

.be-logo--dark .be-logo__empty {
	color: rgba(255, 255, 255, 0.55);
}

.be-logo__actions {
	display: flex;
	gap: 6px;
	margin-top: 6px;
}
</style>

<style>
/* ColorField usa le classi del pannello del builder: qui le stesse misure */
.brand-editor .pp-label {
	display: block;
	margin-bottom: 4px;
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	color: rgba(0, 0, 0, 0.72);
}

.brand-editor .be-label--color-row {
	/* stessa altezza della testata di ColorField, cosi' i tre campi stanno in riga */
	display: flex;
	align-items: center;
	min-height: 22px;
}

/* Icona dentro il campo (social): senza etichetta Vuetify la lascia in alto */
.brand-editor .v-text-field--outlined.v-input--dense .v-input__prepend-inner {
	align-self: center;
	margin-top: 0 !important;
}

.brand-editor .pp-hint {
	margin-top: 6px;
	font-size: 12px;
	line-height: 16px;
	color: rgba(0, 0, 0, 0.6);
}
</style>
